import React from 'react';
import { VBox } from '../layout';
import { ClassNameProp } from '../common-types';
import { PatientDetailsPatientProp } from './internal';
import { PatientDetailsInsurancePlans } from './PatientDetailsInsurancePlans';
import { PatientDetailsPersonDetails } from './PatientDetailsPersonDetails';
import { PatientDetailsPictures } from './PatientDetailsPictures';
import { PatientDetailsContactInformation } from './PatientDetailsContactInformation';
import { PatientDetailsAddress } from './PatientDetailsAddress';
import {
  PatientDetailsIdentifiers,
  PatientDetailsIdentifiersProps
} from './PatientDetailsIdentifiers';

/**
 * Properties for a {@link PatientDetails} component.
 * @author agd
 */
export type PatientDetailsProps = ClassNameProp &
  PatientDetailsPatientProp &
  Pick<PatientDetailsIdentifiersProps, 'filterIdentifiers'> & {
    /** If the qr qode for the patient mobile app should be hidden. */
    mobileAppBarcodeHidden?: boolean;
    /** If the SSN is hidden. */
    ssnHidden?: boolean;
    /** If the address (and by extension the birthplace) is hidden. */
    addressHidden?: boolean;
    /** If the motherMaidenName part is hidden */
    motherMaidenNameHidden?: boolean;
    /** If the insurance details are hidden. */
    insuranceDetailsHidden?: boolean;
    /** If the contact information section is hidden. */
    contactInformationHidden?: boolean;
    /** If the identifiers section is hidden. */
    identifiersHidden?: boolean;
  };

/**
 * PatientDetails component used to display the details of an {@link EnhancedPatient}
 *
 * @param properties a {@link PatientDetailsProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function PatientDetails({
  patient,
  className = '',
  mobileAppBarcodeHidden = false,
  ssnHidden = false,
  addressHidden = false,
  motherMaidenNameHidden = false,
  insuranceDetailsHidden = false,
  contactInformationHidden = false,
  identifiersHidden = false,
  filterIdentifiers
}: PatientDetailsProps): JSX.Element {
  return (
    <VBox className={`ehs-patient-details ${className}`.trim()}>
      <PatientDetailsPictures
        patient={patient}
        mobileAppBarcodeHidden={mobileAppBarcodeHidden}
      />
      <PatientDetailsPersonDetails
        patient={patient}
        ssnHidden={ssnHidden}
        motherMaidenNameHidden={motherMaidenNameHidden}
        birthPlaceHidden={addressHidden}
      />
      {!insuranceDetailsHidden && (
        <PatientDetailsInsurancePlans patient={patient} />
      )}
      {!contactInformationHidden && (
        <PatientDetailsContactInformation patient={patient} />
      )}
      {!addressHidden && <PatientDetailsAddress patient={patient} />}
      {!identifiersHidden && (
        <PatientDetailsIdentifiers
          patient={patient}
          filterIdentifiers={filterIdentifiers}
        />
      )}
    </VBox>
  );
}
